import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import "./App.css";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Today from "./components/today/Today";
import ExpenseTracker from "./components/expense/ExpenseTracker";
import Planner from "./components/planner/Planner";
import AIChatWidget from "./components/ai/AIChatWidget";
import CommandPalette from "./components/shared/CommandPalette";
import CalendarPage from "./components/CalendarPage";
import SearchPage from "./components/search/SearchPage";
import AdminBoard from "./components/admin/AdminBoard";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Login from "./components/auth/Login";
import Signup from "./components/auth/Signup";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import Notes from "./components/notes/Notes";
import Journal from "./components/journal/Journal";
import FileVault from "./components/files/FileVault";
import TasksPage from "./components/tasks/TasksPage";
import TaskForm from "./components/tasks/TaskForm";
import HabitsPage from "./components/habits/HabitsPage";
import DashboardPage from "./components/dashboard/DashboardPage";
import WeeklyReviewPage from "./components/review/WeeklyReviewPage";
import SpacesPage from "./components/spaces/SpacesPage";
import ProjectsPage from "./components/projects/ProjectsPage";
import TopicsPage from "./components/topics/TopicsPage";
import UpcomingDates from "./components/planner/UpcomingDates";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastProvider } from "./components/ToastContext";
import { useAuth, WELCOME_FLAG } from "./components/auth/AuthContext";
import IncomingMessageReviewModal from "./components/incoming/IncomingMessageReviewModal";
import "./components/Toast.css";

function App() {
  const { auth } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  const [incomingReviewOpen, setIncomingReviewOpen] = useState(false);
  const [incomingReviewCount, setIncomingReviewCount] = useState(0);
  const [incomingHoldCount, setIncomingHoldCount] = useState(0);

  useEffect(() => {
    if (!auth?.token) { setShowWelcome(false); return; }
    if (sessionStorage.getItem(WELCOME_FLAG) === "1") {
      setShowWelcome(true);
      sessionStorage.removeItem(WELCOME_FLAG);
    }
  }, [auth?.token]);

  useEffect(() => {
    if (!showWelcome) return undefined;
    const timer = setTimeout(() => setShowWelcome(false), 3000);
    return () => clearTimeout(timer);
  }, [showWelcome]);

  const displayName = auth?.username || "there";

  return (
    <ToastProvider>
      <ErrorBoundary>
        <div className="app-shell" data-authed={auth?.token ? "true" : "false"}>
          <Header
            pendingReviewCount={incomingReviewCount}
            holdReviewCount={incomingHoldCount}
            onReviewClick={() => setIncomingReviewOpen(true)}
          />

          <div className="app-content-area">
            {showWelcome && (
              <div className="welcome-popup" role="status" aria-live="polite">
                <div className="welcome-popup-title">Welcome back</div>
                <div className="welcome-popup-text">Glad to see you, {displayName}.</div>
              </div>
            )}

            <main className="app-main">
              <Routes>
                {/* PUBLIC */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />

                {/* DEFAULT */}
                <Route path="/" element={<ProtectedRoute><Navigate to="/today" /></ProtectedRoute>} />

                {/* DASHBOARD */}
                <Route path="dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />

                {/* TODAY */}
                <Route path="today" element={<ProtectedRoute><Today /></ProtectedRoute>} />
                <Route path="search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />

                {/* FINANCE */}
                <Route path="finance/*" element={<ProtectedRoute><ExpenseTracker /></ProtectedRoute>} />

                {/* PLANNER */}
                <Route path="planner/*" element={<ProtectedRoute><Planner /></ProtectedRoute>} />
                <Route path="calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />

                {/* NOTES & JOURNAL */}
                <Route path="notes/*" element={<ProtectedRoute><Notes /></ProtectedRoute>} />
                <Route path="journal" element={<ProtectedRoute><Journal /></ProtectedRoute>} />

                {/* FILES */}
                <Route path="/files" element={<ProtectedRoute><FileVault /></ProtectedRoute>} />

                {/* TASKS */}
                <Route path="tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
                <Route path="tasks/new" element={<ProtectedRoute><TaskForm /></ProtectedRoute>} />
                <Route path="tasks/edit/:id" element={<ProtectedRoute><TaskForm /></ProtectedRoute>} />

                {/* HABITS */}
                <Route path="habits" element={<ProtectedRoute><HabitsPage /></ProtectedRoute>} />

                {/* WEEKLY REVIEW */}
                <Route path="review" element={<ProtectedRoute><WeeklyReviewPage /></ProtectedRoute>} />

                {/* SHARED SPACES */}
                <Route path="spaces" element={<ProtectedRoute><SpacesPage /></ProtectedRoute>} />

                {/* UPCOMING DATES */}
                <Route path="upcoming" element={<ProtectedRoute><UpcomingDates /></ProtectedRoute>} />

                {/* PROJECTS */}
                <Route path="projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
                <Route path="topics" element={<ProtectedRoute><TopicsPage /></ProtectedRoute>} />

                {/* ADMIN */}
                <Route path="/admin" element={<ProtectedRoute><AdminBoard /></ProtectedRoute>} />
              </Routes>
            </main>

            {auth?.token && <Footer />}
          </div>

          {auth?.token && <AIChatWidget />}
          {auth?.token && <CommandPalette />}
          {auth?.token && (
            <IncomingMessageReviewModal
              enabled={Boolean(auth?.token)}
              open={incomingReviewOpen}
              onClose={() => setIncomingReviewOpen(false)}
              onCountChange={(payload) => {
                const total = typeof payload === "number" ? payload : payload?.total ?? 0;
                const hold = typeof payload === "number" ? 0 : payload?.hold ?? 0;
                setIncomingReviewCount(total);
                setIncomingHoldCount(hold);
                if (total === 0) setIncomingReviewOpen(false);
              }}
            />
          )}
        </div>
      </ErrorBoundary>
    </ToastProvider>
  );
}

export default App;
